import React, { useState } from 'react';
import { CommentEntry } from '../../types';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

interface CategorizedCommentsProps {
  comments: {
    technique: CommentEntry[];
    clients: CommentEntry[];
    facturation: CommentEntry[];
    divers: CommentEntry[];
  };
  onChange: (comments: {
    technique: CommentEntry[];
    clients: CommentEntry[];
    facturation: CommentEntry[];
    divers: CommentEntry[];
  }) => void;
  readOnly?: boolean;
}

const CategorizedComments: React.FC<CategorizedCommentsProps> = ({
  comments,
  onChange,
  readOnly = false
}) => {
  const [activeTab, setActiveTab] = useState<'technique' | 'clients' | 'facturation' | 'divers'>('technique');
  const [newCommentText, setNewCommentText] = useState('');

  const tabs = [
    { key: 'technique' as const, label: 'Technique', color: 'blue' },
    { key: 'clients' as const, label: 'Clients', color: 'green' },
    { key: 'facturation' as const, label: 'Facturation', color: 'yellow' },
    { key: 'divers' as const, label: 'Divers', color: 'purple' }
  ];

  const addComment = () => {
    if (!newCommentText.trim()) return;

    const newComment: CommentEntry = {
      id: Date.now().toString(),
      text: newCommentText.trim(),
      date: new Date().toISOString(),
      author: 'Utilisateur'
    };

    const updatedComments = {
      ...comments,
      [activeTab]: [...comments[activeTab], newComment]
    };

    onChange(updatedComments);
    setNewCommentText('');
  };

  const removeComment = (commentId: string) => {
    const updatedComments = {
      ...comments,
      [activeTab]: comments[activeTab].filter(comment => comment.id !== commentId)
    };
    onChange(updatedComments);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTabColorClasses = (color: string, isActive: boolean) => {
    const colors = {
      blue: isActive 
        ? 'border-blue-500 text-blue-600 dark:text-blue-400' 
        : 'border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300',
      green: isActive 
        ? 'border-green-500 text-green-600 dark:text-green-400' 
        : 'border-transparent text-gray-500 hover:text-green-600 hover:border-green-300',
      yellow: isActive 
        ? 'border-yellow-500 text-yellow-600 dark:text-yellow-400' 
        : 'border-transparent text-gray-500 hover:text-yellow-600 hover:border-yellow-300',
      purple: isActive 
        ? 'border-purple-500 text-purple-600 dark:text-purple-400' 
        : 'border-transparent text-gray-500 hover:text-purple-600 hover:border-purple-300'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <div className="space-y-4">
      {/* Onglets */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${getTabColorClasses(tab.color, activeTab === tab.key)}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              {comments[tab.key].length > 0 && (
                <span className="ml-2 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full px-2 py-0.5">
                  {comments[tab.key].length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Contenu de l'onglet actif */}
      <div className="space-y-3">
        {/* Formulaire d'ajout (uniquement si pas en lecture seule) */}
        {!readOnly && (
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <div className="space-y-3">
              <textarea
                value={newCommentText}
                onChange={(e) => setNewCommentText(e.target.value)}
                placeholder={`Ajouter un commentaire ${tabs.find(t => t.key === activeTab)?.label.toLowerCase()}...`}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white resize-none"
                rows={3}
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={addComment}
                  disabled={!newCommentText.trim()}
                  className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Liste des commentaires */}
        <div className="space-y-3">
          {comments[activeTab].length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              Aucun commentaire {tabs.find(t => t.key === activeTab)?.label.toLowerCase()}
            </p>
          ) : (
            comments[activeTab]
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
              .map((comment) => (
                <div
                  key={comment.id}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                        {comment.text}
                      </p>
                      <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <span>{formatDate(comment.date)}</span>
                        {comment.author && (
                          <>
                            <span className="mx-2">•</span>
                            <span>{comment.author}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => removeComment(comment.id)}
                        className="ml-4 p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        title="Supprimer ce commentaire"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CategorizedComments; 